import { VehicleType } from '../../graphql/types';
import type { CarlineGroup, CarlineQuery } from '../../@types/mapped';

export interface Carline extends CarlineQuery {
  group: string;
}

export type DisplayedVehicleType = 'BEV' | 'PHEV' | 'ICEV';
export const vehicleTypes: DisplayedVehicleType[] = [
  VehicleType.Bev,
  VehicleType.Phev,
  VehicleType.Icev,
];

export const getDefaultVehicleType = (): DisplayedVehicleType[] => [];

export const flattenCarlineGroups = (carlineGroups: CarlineGroup[]): Array<Carline> =>
  carlineGroups.flatMap((carlineGroup) =>
    carlineGroup.carlines.map((carline) => ({ ...carline, group: carlineGroup.identifier.id })),
  );

export const filterByCarlineGroup = (carlines: Carline[], selectedCarlineGroups: string[]) =>
  carlines.filter(
    (carline) =>
      selectedCarlineGroups.length === 0 || selectedCarlineGroups.includes(carline.group),
  );

export const filterByBodyType = (carlines: Carline[], selectedBodyTypes: string[]) =>
  carlines.filter((carline) => {
    // we need to filter bodyType by the `bodyType` key and also the `categories` due to inconsistent data
    if (selectedBodyTypes.length === 0) {
      return true;
    }
    if (carline.bodyTypes && carline.bodyTypes.length > 0) {
      // eslint-disable-next-line no-restricted-syntax
      for (const bodyType of carline.bodyTypes) {
        if (selectedBodyTypes.includes(bodyType!)) {
          return true;
        }
      }
    }
    return false;
  });

export const filterCarlineGroupsByVehicleType = (
  carlineGroups: CarlineGroup[],
  selectedVehicleType: DisplayedVehicleType[] | undefined,
) => {
  if (!selectedVehicleType?.length) {
    return carlineGroups;
  }
  // All types selected => effectively unfiltered (early return)
  if (
    selectedVehicleType.length === vehicleTypes.length &&
    vehicleTypes.every((vt) => selectedVehicleType.includes(vt))
  ) {
    return carlineGroups;
  }
  return carlineGroups
    .map((carlineGroup) => {
      const filteredCarlines = carlineGroup.carlines.filter((carline) =>
        selectedVehicleType.includes(carline.vehicleType as DisplayedVehicleType),
      );
      return {
        ...carlineGroup,
        carlines: filteredCarlines,
      };
    })
    .filter((carlineGroup) => carlineGroup.carlines.length > 0);
};

export const insertNewCarlineGroup = (
  groups: CarlineGroup[],
  flattened: Carline[],
): CarlineGroup[] => {
  const newCarlines = flattened.filter((c) => c.markAsNew);
  if (newCarlines.length === 0) return groups;

  const newGroup: CarlineGroup = {
    identifier: { id: 'new', __typename: 'CarlineIdentifier' },
    name: 'New',
    __typename: 'CarlineGroup',
    carlines: newCarlines,
  };

  return [newGroup, ...groups];
};

export const uniqueByFirst = <T, Key>(items: T[], getKey: (item: T) => Key): T[] => {
  const encounteredKeys = new Set<Key>();

  return items.reduce((uniqueList: T[], item) => {
    const key = getKey(item);
    if (!encounteredKeys.has(key)) {
      encounteredKeys.add(key);
      uniqueList.push(item);
    }
    return uniqueList;
  }, []);
};
